// src/pages/TermsConditions.tsx
import React, { useState, useEffect } from 'react';
import { Plus, Search, FileText, Mail, File } from 'lucide-react';
import { TermsConditionsForm } from '../components/TermsconditionForm';
import { TermsConditionsList } from '../components/TermsconditionList';
import termsConditionsApi from '../api/TermsconditionApi';
import { TermsConditions as TermsConditionsType } from '../types/termscondition';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { Popup } from '../components/Popup';
import ConfirmDialog, { ConfirmState } from '../components/ConfirmDialog';

export function TermsConditions() {
  const [termsList, setTermsList] = useState<TermsConditionsType[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TermsConditionsType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const { showError, showSuccess, popup, closePopup } = usePermissionGuard();

  useEffect(() => {
    fetchTermsConditions();
  }, []);

  const fetchTermsConditions = async () => {
    setLoading(true);
    try {
      const response = await termsConditionsApi.getTermsConditionsList();
      console.log('Terms Conditions API response:', response);
      
      // Handle different response structures
      let data = [];
      if (response && response.data && Array.isArray(response.data)) {
        data = response.data;
      } else if (response && Array.isArray(response)) {
        data = response;
      } else if (response && response.terms && Array.isArray(response.terms)) {
        data = response.terms;
      } else if (response && typeof response === 'object') {
        data = response.data || [];
      } else {
        data = [];
      }
      
      console.log('Processed terms data:', data);
      setTermsList(data);
    } catch (err: any) {
      console.error('Failed to fetch terms:', err);
      showError('FETCH_ERROR', err.response?.data?.message || err.message || 'Failed to load terms');
      setTermsList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: any) => {
    console.log('Creating terms:', data);
    try {
      const response = await termsConditionsApi.createTermsConditions(data);
      console.log('Create response:', response);
      if (response.success !== false) {
        showSuccess('Success', 'Terms & Conditions created successfully');
        await fetchTermsConditions();
        setIsFormOpen(false);
      } else {
        showError('CREATE_ERROR', response.message || 'Failed to create terms');
      }
    } catch (err: any) {
      console.error('Failed to create terms:', err);
      showError('CREATE_ERROR', err.response?.data?.detail?.[0]?.msg || err.message || 'Failed to create terms');
    }
  };

  const handleUpdate = async (data: any) => {
    if (!editingItem?.id) return;
    console.log('Updating terms:', editingItem.id, data);
    try {
      const response = await termsConditionsApi.updateTermsConditions(editingItem.id, data);
      console.log('Update response:', response);
      if (response.success !== false) {
        showSuccess('Success', 'Terms & Conditions updated successfully');
        await fetchTermsConditions();
        setIsFormOpen(false);
        setEditingItem(null);
      } else {
        showError('UPDATE_ERROR', response.message || 'Failed to update terms');
      }
    } catch (err: any) {
      console.error('Failed to update terms:', err);
      showError('UPDATE_ERROR', err.response?.data?.detail?.[0]?.msg || err.message || 'Failed to update terms');
    }
  };

  const handleDelete = async (id: number) => {
    setConfirm({
      title: 'Are you sure you want to delete this Terms & Conditions?',
      description: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        try {
          const response = await termsConditionsApi.deleteTermsConditions(id);
          console.log('Delete response:', response);
          if (response.success !== false) {
            showSuccess('Success', 'Terms & Conditions deleted successfully');
            await fetchTermsConditions();
          } else {
            showError('DELETE_ERROR', response.message || 'Failed to delete terms');
          }
        } catch (err: any) {
          console.error('Failed to delete terms:', err);
          showError('DELETE_ERROR', err.response?.data?.detail?.[0]?.msg || err.message || 'Failed to delete terms');
        }
      },
    });
  };

  // Safe filtering
  const filteredTerms = Array.isArray(termsList) ? termsList.filter(tc =>
    tc?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tc?.letter_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tc?.content?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // Statistics
  const uniqueLetterTypes = Array.isArray(termsList) 
    ? [...new Set(termsList.map(t => t.letter_type).filter(Boolean))]
    : [];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Terms & Conditions Management</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your organization's terms and conditions</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Documents</p>
              <p className="text-2xl font-bold text-blue-900">{Array.isArray(termsList) ? termsList.length : 0}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Letter Types</p>
              <p className="text-2xl font-bold text-purple-900">{uniqueLetterTypes.length}</p>
            </div>
            <Mail className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Search Results</p>
              <p className="text-2xl font-bold text-green-900">{filteredTerms.length}</p>
            </div>
            <Search className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">Total Content</p>
              <p className="text-2xl font-bold text-orange-900">
                {Array.isArray(termsList) 
                  ? termsList.reduce((sum, t) => sum + (t.content?.length || 0), 0).toLocaleString()
                  : 0}
              </p>
            </div>
            <File className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by title, letter type, or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          onClick={() => {
            setEditingItem(null);
            setIsFormOpen(true);
          }}
          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add New Terms
        </button>
      </div>

      {/* Terms & Conditions List */}
      <TermsConditionsList
        termsList={filteredTerms}
        onEdit={(item) => {
          setEditingItem(item);
          setIsFormOpen(true);
        }}
        onDelete={handleDelete}
        loading={loading}
      />

      {/* Form Modal */}
      <TermsConditionsForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingItem(null);
        }}
        onSubmit={editingItem ? handleUpdate : handleCreate}
        initialData={editingItem}
        isLoading={loading}
      />

      <Popup
        isOpen={popup.isOpen}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}